/**
 * Выбор времени набора.
 *
 * Здесь сменилось три подхода. Сначала время вводили строкой по образцу
 * «21:00 или 09.08 21:00»: формат приходилось помнить, а опечатку показывал
 * только отказ сервера. Потом были барабаны часов и минут, как в будильнике
 * телефона, — на телефоне они хороши, но админку открывают с компьютера, где
 * барабан крутят мышью по одной строке, и до нужного часа приходилось ехать
 * полминуты.
 *
 * Сейчас это поле ввода со списком — то, что советуют для настольных
 * интерфейсов: кто знает время, набирает его с клавиатуры («21», «2100»,
 * «21:00» — всё подойдёт), кто не знает — выбирает из списка с шагом в
 * четверть часа или жмёт кнопку вроде «через час». Стрелки ходят по списку,
 * Enter выбирает, Esc закрывает.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import {
  dayOfChoice,
  parseTypedTime,
  slotMatches,
  timeAfter,
  timeSlots,
  type TimeSlot,
} from '../lib/event-time.ts';

/** Быстрые кнопки: столько минут от текущего момента. */
const QUICK_OFFSETS: { minutes: number; label: string }[] = [
  { minutes: 30, label: 'через 30 мин' },
  { minutes: 60, label: 'через час' },
  { minutes: 120, label: 'через 2 часа' },
];

export function TimePicker({
  value,
  timezone,
  onChange,
}: {
  /** Время вида `21:00`. Пустая строка — ещё не выбрано. */
  value: string;
  timezone: string;
  onChange: (value: string) => void;
}): ReactNode {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Значение может прийти со стороны: при переносе времени поле открывается
  // уже заполненным.
  useEffect(() => {
    setQuery(value);
  }, [value]);

  /*
   * Список пересчитывается только при смене пояса: пересобирать его на
   * каждый удар по клавише незачем — слоты идут с шагом в четверть часа,
   * и минута туда-сюда ничего в них не меняет.
   */
  const slots = useMemo(() => timeSlots(timezone), [timezone]);
  const visible = useMemo(() => slots.filter((slot) => slotMatches(slot, query)), [slots, query]);

  const day = dayOfChoice(value, timezone);
  const typed = parseTypedTime(query);
  // Набрано что-то, чего в списке нет: 21:07 — законное время, просто не слот.
  const offGrid = typed !== null && typed === value && !slots.some((slot) => slot.time === typed);

  useEffect(() => {
    setActive(0);
  }, [query]);

  // Подсвеченная строка держится в виду: стрелками уезжают далеко вниз.
  useEffect(() => {
    if (!open) {
      return;
    }

    listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  // Щелчок мимо закрывает список, но не трогает выбранное значение.
  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: PointerEvent): void => {
      if (boxRef.current !== null && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const choose = (time: string): void => {
    onChange(time);
    setQuery(time);
    setOpen(false);
  };

  /** Набранное руками принимается как есть: 21:07 — тоже время. */
  const commitTyped = (): void => {
    const parsed = parseTypedTime(query);
    if (parsed !== null) {
      onChange(parsed);
      setQuery(parsed);
    } else {
      // Разобрать не вышло — возвращаем поле к выбранному, чтобы в нём не
      // осталось того, чего сервер не примет.
      setQuery(value);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActive((current) => {
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
        return Math.max(0, Math.min(visible.length - 1, next));
      });
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const slot = open ? visible[active] : undefined;
      if (slot !== undefined) {
        choose(slot.time);
      } else {
        commitTyped();
        setOpen(false);
      }
      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="space-y-2" ref={boxRef}>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={query}
          placeholder="21:00"
          aria-label="Время начала"
          aria-expanded={open}
          role="combobox"
          aria-controls="time-picker-list"
          autoComplete="off"
          className="w-full rounded-xl px-4 py-2.5 text-lg tabular-nums outline-none"
          style={{
            background: 'var(--surface-panel)',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-primary)',
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onBlur={commitTyped}
          onKeyDown={onKeyDown}
        />

        {open && visible.length > 0 && (
          <ul
            id="time-picker-list"
            ref={listRef}
            role="listbox"
            className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-xl py-1"
            style={{
              background: 'var(--surface-panel)',
              border: '1px solid var(--border-subtle)',
              boxShadow: '0 12px 32px rgb(0 0 0 / 0.28)',
            }}
          >
            {visible.map((slot: TimeSlot, index) => {
              const selected = slot.time === value;

              return (
                <li key={slot.time}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className="flex w-full items-center justify-between px-4 py-2 text-left tabular-nums"
                    style={{
                      background: index === active ? 'var(--surface-hover)' : 'transparent',
                      color: selected ? 'var(--accent)' : 'var(--text-primary)',
                      fontWeight: selected ? 600 : 400,
                    }}
                    // Нажатие мыши приходит раньше потери фокуса: без этого
                    // поле успевало бы вернуть прежнее значение.
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onMouseEnter={() => {
                      setActive(index);
                    }}
                    onClick={() => {
                      choose(slot.time);
                    }}
                  >
                    <span>{slot.time}</span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {slot.tomorrow ? 'завтра' : 'сегодня'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {QUICK_OFFSETS.map((offset) => (
          <button
            key={offset.minutes}
            type="button"
            className="cursor-pointer rounded-lg px-3 py-1.5 text-sm"
            style={{ background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}
            onClick={() => {
              choose(timeAfter(offset.minutes, timezone));
            }}
          >
            {offset.label}
          </button>
        ))}
      </div>

      {/* Подпись повторяет правило сервера: время, которое сегодня уже
          прошло, означает завтрашний день. */}
      <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        {day === '' ? (
          'Наберите время или выберите из списка'
        ) : (
          <>
            Начало: <strong className="tabular-nums">{value}</strong>, {day}
            {offGrid && <span style={{ color: 'var(--text-muted)' }}> · время задано вручную</span>}
          </>
        )}
      </div>
    </div>
  );
}
